import { RoleTypes } from 'src/models/roles';
import SigningForm from 'src/models/SigningForm';
import { StudentReferences, User } from 'src/models/user';
import { isAdvisorMidSemesterConfirmEnabled } from 'src/utils/featureFlags';

export function endsemesterFormsActive(signingForm: SigningForm) {
  return !!signingForm.midSemesterCompletedDate
    && new Date(signingForm.midSemesterCompletedDate) <= new Date();
}

export function midsemesterFormIsDisabled(signingForm: SigningForm, studentReferences: StudentReferences | undefined, loggedInUser: User) {
  return signingForm.isLocked || (!isPrimaryTeacher(loggedInUser, studentReferences) && !canAdvisorConfirmMidSemester(loggedInUser, studentReferences)) || signingForm.midSemesterCompletedDate != null || signedByStudent(signingForm) || signedBySupervisor(signingForm);
}

export function partOneEndSemesterFormIsDisabled(signingForm: SigningForm, studentReferences: StudentReferences | undefined, loggedInUser: User) {
  return signingForm.isLocked || (!isTeacher(loggedInUser) && !canAdvisorConfirmEndSemester(loggedInUser, studentReferences)) || signingForm.midSemesterCompletedDate == null || signedByStudent(signingForm) || signedBySupervisor(signingForm);
}

export function endsemesterFormIsDisabled(signingForm: SigningForm, loggedInUser: User) {
  return signingForm.isLocked || !isTeacher(loggedInUser) || signingForm.midSemesterCompletedDate == null;
}

export function signedByLoggedInUser(signingForm: SigningForm, loggedInUser: User) {
  if (isTeacher(loggedInUser) && signedByLoggedInTeacher(signingForm, loggedInUser)) return true;
  if (isSupervisor(loggedInUser) && signedByLoggedInSupervisor(signingForm, loggedInUser)) return true;
  if (isStudent(loggedInUser) && signedByLoggedInStudent(signingForm, loggedInUser)) return true;
  return signedByLoggedInSubjectResponsible(signingForm, loggedInUser);
}

export function signedByAll(signingForm: SigningForm) {
  return signedByTeacher(signingForm) && signedBySupervisor(signingForm) && signedByStudent(signingForm);
}

export function loggedInUserCanSign(signingForm: SigningForm, loggedInUser: User) {
  if (signedByLoggedInUser(signingForm, loggedInUser)) return false;
  if (signingForm.isLocked) return false;
  if (!isTeacher(loggedInUser) && !isStudent(loggedInUser) && !isSupervisor(loggedInUser)) return false;

  return true;
}

export function showSignButton(signingForm: SigningForm, loggedInUser: User) {
  if (signingForm.isLocked) return false;
  if (signedByLoggedInUser(signingForm, loggedInUser)) return false;
  return true;
}

export function teacherWaitingForOtherSignatures(signingForm: SigningForm, loggedInUser: User) {
  if (!isTeacher(loggedInUser)) return false;
  if (!signedBySupervisor(signingForm)) return true;
  if (!signedByStudent(signingForm)) return true;
  return false;
}

export function teacherWaitingForSupervisorSignature(signingForm: SigningForm, loggedInUser: User) {
  if (!isTeacher(loggedInUser)) return false;
  if (!signedBySupervisor(signingForm)) return true;
  return false;
}

export function noOneSigned(signingForm: SigningForm) {
  return !(signedByStudent(signingForm) || signedBySupervisor(signingForm) || signedByTeacher(signingForm));
}

export function showSaveButton(signingForm: SigningForm, loggedInUser: User) {
  if (signingForm.isLocked) return false;
  if (!isTeacher(loggedInUser)) return false;
  return true;
}

export function showSaveButtonForPrimaryTeacher(signingForm: SigningForm, studentReferences: StudentReferences | undefined, loggedInUser: User) {
  if (signingForm.isLocked) return false;
  if (!isPrimaryTeacher(loggedInUser, studentReferences) && !canAdvisorConfirmMidSemester(loggedInUser, studentReferences)) return false;
  return true;
}

export function signingIsUnavailable(signingForm: SigningForm, loggedInUser: User, studentReferences?: StudentReferences): boolean | (() => boolean) {
  // The advisor confirms part one and signs in one action, which is what sets endSemesterCompletedDate,
  // so it can't be a precondition for them.
  if (canAdvisorConfirmEndSemester(loggedInUser, studentReferences)) return signingForm.midSemesterCompletedDate === null;
  return (isTeacher(loggedInUser) && signingForm.midSemesterCompletedDate === null) || ((signingForm.midSemesterCompletedDate === null || signingForm.endSemesterCompletedDate === null) && !isTeacher(loggedInUser));
}

export function getDefaultSigningForm(student: User): SigningForm {
  return {
    id: -1,
    studentReferencesId: student.studentReferences?.id ?? -1,
    attendancePassed: null,
    workRequirementsPassed: null,
    specificCompetenceAreasPassed: null,
    generalCompetenceAreasPassed: null,
    teacherComment: null,
    overallPassed: null,
    isLocked: false,
    endSemesterCompletedDate: null,
    midSemesterCompletedDate: null,
    midSemesterSpecificCompetenceAreasBegun: null,
    midSemesterStudentHasGivenFeedback: null,
    signedByAdvisorDate: null,
    signedByAdvisorUserId: null,
    signedByStudentDate: null,
    signedByStudentUserId: null,
    signedBySubjectResponsibleUserId: null,
    signedByTeacherDate: null,
    signedByTeacherUserId: null,
    signWithoutAdvisorConfirmedDate: null,
    signedBySubjectResponsibleDate: null,
  };
}

export const isPrimaryTeacher = (user: User, studentReferences: StudentReferences | undefined) => isTeacher(user) && user.id === studentReferences?.teacherUserId;
export const isPrimaryAdvisor = (user: User, studentReferences: StudentReferences | undefined) => isSupervisor(user) && user.id === studentReferences?.advisorUserId;
export const canAdvisorConfirmMidSemester = (user: User, studentReferences: StudentReferences | undefined) => isAdvisorMidSemesterConfirmEnabled() && isPrimaryAdvisor(user, studentReferences);
export const canAdvisorCreateSigningForm = (user: User, studentReferences: StudentReferences | undefined) => isAdvisorMidSemesterConfirmEnabled() && isPrimaryAdvisor(user, studentReferences);
export const canAdvisorCancelSigningForm = (user: User, studentReferences: StudentReferences | undefined) => isAdvisorMidSemesterConfirmEnabled() && isPrimaryAdvisor(user, studentReferences);
export const canAdvisorConfirmEndSemester = (user: User, studentReferences: StudentReferences | undefined) => isAdvisorMidSemesterConfirmEnabled() && isPrimaryAdvisor(user, studentReferences);
export const isTeacher = (user: User) => user.roles.some(r => r.name === RoleTypes.Teacher);
const isSupervisor = (user: User) => user.roles.some(r => r.name === RoleTypes.Advisor);
const isStudent = (user: User) => user.roles.some(r => r.name === RoleTypes.Student);

export const signedByTeacher = (signingForm: SigningForm) => !!signingForm.signedByTeacherUserId;
export const signedBySupervisor = (signingForm: SigningForm) => !!signingForm.signedByAdvisorUserId;
export const signedByStudent = (signingForm: SigningForm) => !!signingForm.signedByStudentUserId;
export const signedBySubjectResponsible = (signingForm: SigningForm) => !!signingForm.signedBySubjectResponsibleUserId;

export function signingFormCompleted(signingForm: SigningForm) {
  return signingForm.endSemesterCompletedDate != null
    && signedByTeacher(signingForm)
    && (signedBySupervisor(signingForm) || signedBySubjectResponsible(signingForm));
}

export function signingFormPendingSignature(signingForm: SigningForm) {
  return signingForm.endSemesterCompletedDate != null && !signingFormCompleted(signingForm);
}

export type SigningFormPhase = 'midterm' | 'endterm';

export function getSigningFormPhase(signingForm: SigningForm | null): SigningFormPhase {
  if (!signingForm) return 'midterm';
  return endsemesterFormsActive(signingForm) ? 'endterm' : 'midterm';
}
const signedByLoggedInTeacher = (signingForm: SigningForm, loggedInUser: User) => !!signingForm.signedByTeacherUserId && signingForm.signedByTeacherUserId === loggedInUser.id;
const signedByLoggedInSupervisor = (signingForm: SigningForm, loggedInUser: User) => !!signingForm.signedByAdvisorUserId && signingForm.signedByAdvisorUserId === loggedInUser.id;
const signedByLoggedInStudent = (signingForm: SigningForm, loggedInUser: User) => !!signingForm.signedByStudentUserId && signingForm.signedByStudentUserId === loggedInUser.id;
const signedByLoggedInSubjectResponsible = (signingForm: SigningForm, loggedInUser: User) => !!signingForm.signedBySubjectResponsibleUserId && signingForm.signedBySubjectResponsibleUserId === loggedInUser.id;
